import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { on } from '@/lib/events';
import { useSelectedSets } from './store';

export interface FlyToPayload {
  lat?: number;
  lon?: number;
  zoom?: number;
}

export function FlyToListener() {
  const map = useMap();
  const sets = useSelectedSets();
  const { data: meta } = useQuery(queries.meta());
  // F1.7 lazy: we only need istat / lat / lon / prov / reg_code for flyTo —
  // all present in light (~400 KB, already loaded on boot). Avoid pulling
  // full comuni.json (~3.6 MB) just to compute bounds.
  const { data: comuni } = useQuery({
    ...queries.light(meta?.version ?? ''),
    enabled: !!meta?.version,
  });

  useEffect(() => {
    const offPoint = on<FlyToPayload>('places:flyToPoint', (payload) => {
      if (!payload || payload.lat == null || payload.lon == null) return;
      map.flyTo([payload.lat, payload.lon], payload.zoom ?? 11, { duration: 0.5 });
    });

    const offPoints = on<{ points: Array<{ lat: number; lon: number }> }>(
      'places:flyToBounds',
      (payload) => {
        if (!payload || !payload.points || payload.points.length === 0) return;
        const bounds = L.latLngBounds(payload.points.map((p) => [p.lat, p.lon]));
        map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 11, duration: 0.6 });
      },
    );

    const offSelection = on('places:flyToSelection', () => {
      if (!comuni || sets.total === 0) return;
      const matching = comuni.filter(
        (c) =>
          sets.comuni.has(c.istat) ||
          sets.provinces.has(c.prov) ||
          sets.regions.has(String(c.reg_code)),
      );
      if (matching.length === 0) return;
      const bounds = L.latLngBounds(matching.map((c) => [c.lat, c.lon]));
      map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 11, duration: 0.6 });
    });

    return () => {
      offPoint();
      offPoints();
      offSelection();
    };
  }, [map, comuni, sets]);

  return null;
}
